import {
  DefaultFetchError,
  FetchCreate,
  FetchDelete,
  FetchGet,
  FetchGetId,
  FetchSuccess,
  FetchUpdate,
  PagingDataResponse,
  PagingParams,
  useFetchCreate,
  useFetchDelete,
  useFetchGet,
  useFetchGetId,
  useFetchUpdate,
} from "../fetch";
import dayjs, { Dayjs } from "dayjs";
import { User, UserStatusType } from "../../store/auth";
import { Vaad } from "./vaads";

export interface UsersGetParams extends PagingParams {
  fullName?: string;
  orderByColumn?: "username" | "firstName";
  vaadId?: string;
  vaadName?: string;
}

export type VaadRole = "student" | "admin" | "moderator" | string;
export type UserStatus =
  | "new_lead"
  | "form_sent"
  | "confirmed"
  | "invited"
  | "active"
  | "inactive"
  | "archived"
  | string;
export type VaadStatus = "not_started" | "active" | "completed" | string;
export type SubscriptionStatus =
  | "active"
  | "trialing"
  | "incomplete"
  | "incomplete_expired"
  | "past_due"
  | "canceled"
  | "unpaid"
  | "paused"
  | "payment_failed"
  | string;
export type RegistrationAction = "send_registration_link" | string;

export interface UserMembership {
  id: string;
  vaad: Vaad;
  vaadRole: VaadRole;
  userStatus: UserStatus;
  subscriptionStatus?: SubscriptionStatus | null;
  discountValue?: number | null;
  discountEndDate?: string | null;
  numberOfMonths?: number | null;
  stripeManageUrl?: string | null;
  promotionCode?: string | null;
  stripeSubscriptionId?: string | null;
  stripeCustomerId?: string | null;
  stripePriceId?: string | null;
  subscriptionCurrentPeriodStart?: string | null;
  subscriptionTill?: string | null;
  subscriptionCanceledAt?: string | null;
  subscriptionCancelAtPeriodEnd?: boolean | null;
  stripeLastEventId?: string | null;
  stripeLastEventType?: string | null;
  stripeLastEventAt?: string | null;
  vaadName: string;
  vaadId?: string | null;
  vaadStatus: VaadStatus;
  registrationAction: RegistrationAction;
  meetingSlots?: string[];
  subscriptionType?: "monthly" | "yearly";
}

export interface MembershipCreateParams {
  user?: { id: string };
  vaad: { id: string };
  discountValue?: number;
  discountEndDate?: any;
  numberOfMonths?: number;
  subscriptionType?: "monthly" | "yearly";
}

export const useMembershipCreate = (): FetchCreate<
  MembershipCreateParams,
  DefaultFetchError,
  MembershipCreateParams
> => useFetchCreate("users/memberships");

export interface LandingMeetingTime {
  time: string;
  answer: string;
}

export interface LandingFormData {
  formType?: string;
  vaadId?: string;
  vaadName?: string;
  questionTitle?: string;
  question?: string;
  city?: string;
  meetingTimes?: LandingMeetingTime[];
  coordinate?: string | null;
  watchedIntro?: boolean;
  readInformation?: boolean;
  readDetails?: string | null;
  submittedAt?: string;
}

export interface UserParams extends Omit<
  User,
  | "birthdate"
  | "settings"
  | "isTwoFAActive"
  | "firstName"
  | "lastName"
  | "status"
> {
  id: string;
  birthdate?: Dayjs | null;
  createdAt?: string;
  photoUrl?: string;
  status?: UserStatusType;
  memberships?: UserMembership[];
  landingFormData?: LandingFormData | null;
}

export interface UserCreateParams extends Omit<
  User,
  | "birthdate"
  | "settings"
  | "isTwoFAActive"
  | "firstName"
  | "lastName"
  | "status"
> {
  birthdate?: Dayjs | null;
  memberships?: MembershipCreateParams[];
}

export type UserUpsetParams = Omit<UserCreateParams, "id" | "status">;

interface AdministratorsGetParams extends PagingParams {
  orderByColumn?: "username" | "firstName" | "createdAt";
}

export const useUsersGetAll = <DD = UsersTableData>(
  decorateData?: (data: PagingDataResponse<User>) => DD,
): FetchGet<
  PagingDataResponse<User>,
  AdministratorsGetParams,
  DefaultFetchError,
  DD
> =>
  useFetchGet("users", {
    autoStart: false,
    startStateLoading: false,
    decorateData,
  });

export interface UserTableRow extends Omit<User, "settings"> {
  key: string;
  subscriptionStatus?: string;
  memberships?: UserMembership[];
  // fullName: string;
}

export interface UsersTableData {
  data: UserTableRow[];
  total: number;
}

export const useUsersTableGet = () =>
  useUsersGetAll<UsersTableData>(
    ({ data, meta }: PagingDataResponse<User>): UsersTableData => ({
      data: data.map(
        ({ settings, ...item }: User): UserTableRow => ({
          key: item.id,
          // fullName: `${item.firstName || "-"}`,
          ...item,
        }),
      ),
      total: meta.itemCount,
    }),
  );

export const useUserGetById = <DD = UserParams>(
  id?: string,
  decorateData?: (data: UserParams) => DD,
): FetchGetId<UserParams, DefaultFetchError, unknown, DD> =>
  useFetchGetId("users", id, {
    autoStart: !!id,
    decorateData,
  });

export const useUserCreate = (): FetchCreate<
  UserCreateParams,
  DefaultFetchError,
  UserUpsetParams
> => useFetchCreate("users");

export type UserUpdateParams = Partial<UserCreateParams>;

export const useUserUpdate = (
  id?: string,
): FetchUpdate<User, DefaultFetchError, UserUpdateParams> =>
  useFetchUpdate("users", id);

export const useUserDelete = (
  id?: string,
): FetchDelete<FetchSuccess, DefaultFetchError> => useFetchDelete("users", id);

export const useUsersGet = (): FetchGet<
  PagingDataResponse<User>,
  UsersGetParams
> => useFetchGet("users", { autoStart: false, startStateLoading: false });

export default {
  useUsersGet,
};

export const useSendRegistrationLink = (
  id?: string,
  membershipId?: string,
): FetchCreate<FetchSuccess, DefaultFetchError, void> =>
  useFetchCreate("users", {
    autoStart: false,
    startStateLoading: false,
  });
export const useRenewSubscription = (
  id?: string,
  membershipId?: string,
): FetchCreate<FetchSuccess, DefaultFetchError, void> =>
  useFetchCreate("users", {
    autoStart: false,
    startStateLoading: false,
  });
export const useCancelSubscription = (): FetchCreate<
  FetchSuccess,
  DefaultFetchError,
  void
> =>
  useFetchCreate("users", {
    autoStart: false,
    startStateLoading: false,
  });
